import { Dialog, Transition } from "@headlessui/react"
import { XIcon } from "@heroicons/react/outline"
import axios from "axios"
import React, { Fragment, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

/*
 * Popup shown when a visitor arrives: the live campaign or sponsored ad set by admins in the
 * regional admin (user-service popup-campaigns). How often a visitor sees the same campaign
 * follows its frequency: every visit (browser session), once a day, or only once.
 */

const API = `${process.env.REACT_APP_BASE_URL}user-service/api/v1/popup-campaigns/`
const DAY_MS = 24 * 60 * 60 * 1000

const seenKey = (campaign) => `byduy_popup_seen_${campaign.id}`

/* storage can be missing or blocked (private mode, webviews): then the popup just shows */
function read(storage, key) {
	try {
		return storage.getItem(key)
	} catch (e) {
		return null
	}
}

function write(storage, key, value) {
	try {
		storage.setItem(key, value)
	} catch (e) {
		// ignore
	}
}

export function shouldShow(campaign, now = Date.now()) {
	if (campaign.frequency === "ONCE") return !read(localStorage, seenKey(campaign))
	if (campaign.frequency === "DAILY") {
		const last = Number(read(localStorage, seenKey(campaign)))
		return !last || now - last >= DAY_MS
	}
	return !read(sessionStorage, seenKey(campaign))
}

export function markSeen(campaign, now = Date.now()) {
	const storage = campaign.frequency === "SESSION" ? sessionStorage : localStorage
	write(storage, seenKey(campaign), String(now))
}

function track(campaign, event) {
	axios.post(`${API}${campaign.id}/track/`, { event }).catch(() => {})
}

export default function PopupCampaign() {
	const [campaign, setCampaign] = useState(null)
	const [open, setOpen] = useState(false)
	const navigate = useNavigate()

	useEffect(() => {
		let cancelled = false
		axios
			.get(`${API}active/`)
			.then((res) => {
				if (cancelled || !res.data || !res.data.image || !shouldShow(res.data)) return
				setCampaign(res.data)
				setOpen(true)
				markSeen(res.data)
				track(res.data, "view")
			})
			.catch(() => {})
		return () => {
			cancelled = true
		}
	}, [])

	if (!campaign) return null

	const close = () => setOpen(false)

	const openLink = () => {
		if (!campaign.link_url) return
		track(campaign, "click")
		close()
		if (campaign.link_url.startsWith("/")) {
			navigate(campaign.link_url)
		} else {
			window.open(campaign.link_url, "_blank", "noopener,noreferrer")
		}
	}

	return (
		<Transition.Root show={open} as={Fragment}>
			<Dialog as='div' className='relative z-50' onClose={close}>
				<Transition.Child as={Fragment} enter='ease-out duration-300' enterFrom='opacity-0' enterTo='opacity-100' leave='ease-in duration-200' leaveFrom='opacity-100' leaveTo='opacity-0'>
					<div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
				</Transition.Child>

				<div className='fixed inset-0 z-10 overflow-y-auto'>
					<div className='flex min-h-full items-center justify-center p-4 text-center'>
						<Transition.Child as={Fragment} enter='ease-out duration-300' enterFrom='opacity-0 scale-95' enterTo='opacity-100 scale-100' leave='ease-in duration-200' leaveFrom='opacity-100 scale-100' leaveTo='opacity-0 scale-95'>
							<Dialog.Panel className='relative w-full max-w-md sm:max-w-3xl transform transition-all'>
								<button
									type='button'
									className='absolute -top-3 -right-3 z-10 rounded-full bg-white p-1 text-gray-500 shadow hover:text-gray-700 focus:outline-none'
									onClick={close}
								>
									<span className='sr-only'>Close</span>
									<XIcon className='h-6 w-6' aria-hidden='true' />
								</button>

								{campaign.kind === "SPONSORED" && (
									<span className='absolute top-2 left-2 z-10 rounded bg-black bg-opacity-60 px-2 py-0.5 text-xs text-white'>
										Sponsored{campaign.sponsor_name ? ` · ${campaign.sponsor_name}` : ""}
									</span>
								)}

								<button
									type='button'
									onClick={openLink}
									className={`block w-full ${campaign.link_url ? "cursor-pointer" : "cursor-default"}`}
									aria-label={campaign.link_url ? `Open ${campaign.title}` : campaign.title}
								>
									<picture>
										{campaign.mobile_image && <source media='(max-width: 639px)' srcSet={campaign.mobile_image} />}
										<img className='w-full max-h-[80vh] object-contain rounded-lg' src={campaign.image} alt={campaign.title} />
									</picture>
								</button>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition.Root>
	)
}
